import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  actionLabel,
  diffAuditValues,
  formatAuditTimestamp,
} from "@/lib/auditFormat";
import type { AuditLogEntry, AuditLogListResponse } from "@/types/audit";

export interface AuditHistoryProps {
  entityType: string;
  entityId: string;
  /** Nur laden wenn sichtbar (z. B. Modal offen). */
  active?: boolean;
  limit?: number;
}

export function AuditHistory({
  entityType,
  entityId,
  active = true,
  limit = 20,
}: AuditHistoryProps) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!active || !entityId) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      entity_type: entityType,
      entity_id: entityId,
      limit: String(limit),
    });
    api
      .get<AuditLogListResponse>(`/api/v1/audit?${params}`)
      .then((res) => setEntries(res.items))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [active, entityId, entityType, limit]);

  useEffect(() => {
    load();
  }, [load]);

  if (!active) return null;

  return (
    <section className="space-y-3 border-t border-separator pt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-label-primary">Änderungsprotokoll</h3>
        <button
          type="button"
          className="text-xs text-accent hover:underline"
          onClick={load}
          disabled={loading}
        >
          Aktualisieren
        </button>
      </div>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {loading && entries.length === 0 ? (
        <p className="text-sm text-label-secondary">Lade Protokoll…</p>
      ) : null}
      {!loading && entries.length === 0 && !error ? (
        <p className="text-sm text-label-secondary">Noch keine Einträge.</p>
      ) : null}

      <ul className="space-y-2">
        {entries.map((entry) => {
          const diffs = diffAuditValues(entry.old_values, entry.new_values);
          const expanded = expandedId === entry.id;
          return (
            <li
              key={entry.id}
              className="rounded-lg border border-separator bg-surface-secondary/40 px-3 py-2"
            >
              <button
                type="button"
                className="flex w-full flex-wrap items-baseline justify-between gap-2 text-left"
                onClick={() => setExpandedId(expanded ? null : entry.id)}
              >
                <span className="text-sm font-medium text-label-primary">
                  {actionLabel(entry.action)}
                </span>
                <span className="text-xs text-label-secondary">
                  {formatAuditTimestamp(entry.created_at)}
                  {entry.actor_login ? ` · ${entry.actor_login}` : ""}
                </span>
              </button>

              {expanded ? (
                <div className="mt-2 space-y-2">
                  {diffs.length === 0 ? (
                    <p className="text-xs text-label-secondary">
                      Vollständiger Datensatz wurde protokolliert (keine Feldänderung erkannt).
                    </p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-label-secondary">
                          <th className="py-1 pr-2 text-left font-medium">Feld</th>
                          <th className="py-1 pr-2 text-left font-medium">Vorher</th>
                          <th className="py-1 text-left font-medium">Nachher</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diffs.map((row) => (
                          <tr key={row.field} className="border-t border-separator/60">
                            <td className="py-1 pr-2 align-top font-mono text-label-secondary">
                              {row.field}
                            </td>
                            <td className="max-w-[40%] py-1 pr-2 align-top whitespace-pre-wrap break-words text-status-red/90">
                              {row.before}
                            </td>
                            <td className="max-w-[40%] py-1 align-top whitespace-pre-wrap break-words text-status-green">
                              {row.after}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : diffs.length > 0 ? (
                <p className="mt-1 text-xs text-label-secondary">
                  {diffs.length} Feld{diffs.length === 1 ? "" : "er"} geändert — Details anzeigen
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
